import { Component, Inject, Input, OnInit } from '@angular/core';
import {
  AddAdditionalImageDialogComponent,
  AdditionalImageDialogInputData,
  getPrimaryImageOrFirst,
  isMediaImage,
  isMediaInfoPublishable,
  PropertyMediaInfo,
} from '@miq/utils';
import { PACIFIC_TEMPLATES_IMAGE_ORDER } from '../../../../../lib/shared/templates/template-blueprints.const';
import { L10nTranslationService } from 'angular-l10n';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, switchMap } from 'rxjs/operators';
import { GroupBase } from '../../../../models/blueprint.model';
import { DataObject, ReportRecord, ReportView } from '../../../../models/report.model';
import { ReportAllRecordsImage, TemplateField } from '../../../../models/template.model';
import { ReportExportViewService } from '../../../../report-export-view.service';
import { REPORT_EXPORT_CONFIGS_TOKEN, ReportExportConfigurations } from '../../../../report-export-view.token';
import { ReportImagesService } from '../../../../report-images.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import {
  ADDITIONAL_IMAGE_FIELD_ID,
  IMAGE_FIELD_ID,
  LAYOUT_IMAGE_3_ID,
} from '../../../../shared/templates/config/common';
import { ReportResetImagesService } from './report-reset-images.service';

@UntilDestroy()
@Component({
  selector: 'rep-presentation-dynamic-view-image',
  templateUrl: './record-image.component.html',
  styleUrls: ['./record-image.component.scss'],
  standalone: false,
})
export class PresentationDynamicViewImageComponent implements OnInit {
  @Input() reportRecordData: ReportRecord;
  @Input() imageGroups: GroupBase<TemplateField>[];
  @Input() currentView: ReportView;
  @Input() reportId: number;

  public imageField: TemplateField;
  public additionalImageField: TemplateField;
  public secondaryImageField: TemplateField;
  public edpMultimediaURI: string;
  public s3BucketImagesUrl: string;
  public hasAdditionalImage = false;
  public hasSecondaryImage = false;
  public reportAllImages: ReportAllRecordsImage;
  private edpRecordId: number;

  constructor(
    @Inject(REPORT_EXPORT_CONFIGS_TOKEN) reportModuleConfigs: ReportExportConfigurations,
    private notificationService: NotificationService,
    private reportExportViewService: ReportExportViewService,
    private reportImagesService: ReportImagesService,
    public translationService: L10nTranslationService,
    private reportResetImagesService: ReportResetImagesService
  ) {
    this.edpMultimediaURI = reportModuleConfigs.edpMultimediaURI;
    this.s3BucketImagesUrl = reportModuleConfigs.s3BucketImagesUrl;
  }

  ngOnChanges() {
    if (this.reportRecordData != null) {
      this.edpRecordId = this.getEdpInfo();
      (this.imageGroups || []).forEach(imageGroup => {
        this.imageField = (imageGroup.fields || []).find(field => field.id === IMAGE_FIELD_ID);
        this.additionalImageField = (imageGroup.fields || []).find(field => field.id === ADDITIONAL_IMAGE_FIELD_ID);
        this.secondaryImageField = (imageGroup.fields || []).find(field => field.id === LAYOUT_IMAGE_3_ID);
      });
      this.showImages();
    }
  }

  ngOnInit() {
    // used for translations
    this.reportImagesService.onUpdateAdditionalImage$.pipe(untilDestroyed(this)).subscribe(response => {
      this.reportAllImages = response;
      this.showImages();
    });
  }

  ngOnDestroy() {
    // used for destroy the Subscription.
  }

  /**
   * method to add the additional image to the record under report.
   */
  public addImage() {
    const imagesToDisplay: PropertyMediaInfo[] = this.getImagesWithoutPrimary();
    const options = { height: '80%', width: '80%', position: { top: '30px' } };
    const translatedData = {
      close: this.translationService.translate('core.close'),
      selectAttachment: this.translationService.translate('core.selectAttachment'),
      select: this.translationService.translate('recordsClipboard.select'),
      noImagesFound: this.translationService.translate('core.noImagesFound'),
    };
    const data: AdditionalImageDialogInputData = {
      imagesToDisplay,
      multiSelect: false,
      edpMultimediaURI: this.edpMultimediaURI,
      s3BucketImagesUrl: this.s3BucketImagesUrl,
      selectedImageId: null,
      translatedData,
    };
    let selectedImage: PropertyMediaInfo;
    this.notificationService
      .openDialog<PropertyMediaInfo>(AddAdditionalImageDialogComponent, data, options)
      .pipe(
        filter(image => image != null),
        switchMap((image: PropertyMediaInfo) => {
          selectedImage = image;
          return this.reportExportViewService.addAdditionalImage(this.reportId, [
            { edpRecordId: this.edpRecordId, imageId: image.id },
          ]);
        })
      )
      .subscribe((reportImageId: number[]) => {
        const recordImages = this.reportAllImages[this.edpRecordId];
        if (recordImages != null) {
          recordImages.push({
            reportImageId: reportImageId[0],
            imageId: selectedImage.id,
          });
        } else {
          const newRecordImage = [
            {
              reportImageId: reportImageId[0],
              imageId: selectedImage.id,
            },
          ];
          this.reportAllImages[this.edpRecordId] = newRecordImage;
        }
        this.reportImagesService.setReportRecordImages(this.reportAllImages);
      });
  }

  /**
   * this method is to edit the 2,3 images
   * @param index is the index value either of secondary or additional image.
   */
  public editImage(index: number) {
    const selectedImageId = this.reportAllImages[this.edpRecordId][index].imageId;
    const imagesToDisplay: PropertyMediaInfo[] = this.getImagesWithoutPrimary();
    const options = { height: '80%', width: '80%', position: { top: '30px' } };
    const translatedData = {
      close: this.translationService.translate('core.close'),
      selectAttachment: this.translationService.translate('core.selectAttachment'),
      select: this.translationService.translate('recordsClipboard.select'),
      noImagesFound: this.translationService.translate('core.noImagesFound'),
    };
    const data: AdditionalImageDialogInputData = {
      imagesToDisplay,
      multiSelect: false,
      edpMultimediaURI: this.edpMultimediaURI,
      s3BucketImagesUrl: this.s3BucketImagesUrl,
      selectedImageId,
      translatedData,
    };
    let selectedImage: PropertyMediaInfo;
    this.notificationService
      .openDialog<PropertyMediaInfo>(AddAdditionalImageDialogComponent, data, options)
      .pipe(
        filter(image => image != null),
        switchMap((image: PropertyMediaInfo) => {
          selectedImage = image;
          const reportImageId = this.reportAllImages[this.edpRecordId][index].reportImageId;
          return this.reportExportViewService.changeAdditionalImage(this.reportId, reportImageId, image.id);
        })
      )
      .subscribe((reportImageId: number) => {
        this.reportAllImages[this.edpRecordId][index] = {
          reportImageId,
          imageId: selectedImage.id,
        };
        this.reportImagesService.setReportRecordImages(this.reportAllImages);
        this.reportRecordData = { ...this.reportRecordData };
        // the reason why again reassigning is to re generate the image which is not under the reportRecordData.
        // So when there is edit it was not calling
      });
  }

  /**
   * this method is to delete the 2,3 images.
   * @param index is the index value of image either of additional or secondary images.
   */
  public deleteImage(index: number) {
    if (this.reportAllImages && Object.keys(this.reportAllImages).length > 0) {
      const reportImageId = this.reportAllImages[this.edpRecordId][index].reportImageId;
      this.reportExportViewService.deleteAdditionalImage(this.reportId, reportImageId).subscribe(() => {
        this.reportAllImages[this.edpRecordId].splice(index, 1);
        this.reportImagesService.setReportRecordImages(this.reportAllImages);
        this.reportRecordData = { ...this.reportRecordData };
        // the reason why again reassigning is to re generate the image which is not under the reportRecordData.
        // So when there is edit it was not calling
      });
    } else {
      this.hasAdditionalImage = false;
      this.hasSecondaryImage = false;
    }
  }

  /**
   * this method is used to fetch the images without the primary image.
   */
  private getImagesWithoutPrimary() {
    let imagesToDisplay: PropertyMediaInfo[] = [];
    const mediaInfo = (this.reportRecordData.associatedProperty as DataObject).getMediaInformation();
    if (mediaInfo.length) {
      const primaryImage = getPrimaryImageOrFirst(mediaInfo);
      if (primaryImage) {
        imagesToDisplay = mediaInfo.filter(
          (media: PropertyMediaInfo) =>
            media.id !== primaryImage.id && isMediaImage(media) && isMediaInfoPublishable(media)
        );
      }
    }
    return imagesToDisplay;
  }

  private showImages() {
    if (
      this.reportAllImages &&
      Object.keys(this.reportAllImages).length > 0 &&
      this.reportAllImages[this.edpRecordId]
    ) {
      const [additionalImage, secondaryImage] = this.reportAllImages[this.edpRecordId];
      this.hasAdditionalImage = !!additionalImage;
      this.hasSecondaryImage = !!secondaryImage;
    } else {
      this.hasAdditionalImage = false;
      this.hasSecondaryImage = false;
    }
  }

  private getEdpInfo() {
    let entityId = this.reportRecordData.associatedProperty
      ? this.reportRecordData.associatedProperty.entity.id
      : this.reportRecordData.data.entity.id;
    if (PACIFIC_TEMPLATES_IMAGE_ORDER.AVAILABILITY_ORDER.includes(this.currentView.templateId)) {
      entityId = -this.reportRecordData.data.entity.id;
    }
    return entityId;
  }

  public resetImages() {
    this.reportResetImagesService
      .resetReportAdditionalImages(this.reportRecordData, this.reportId, this.currentView.templateId)
      .subscribe(() => {
        this.reportExportViewService.getImages(this.reportId).subscribe((res: ReportAllRecordsImage) => {
          this.reportImagesService.setReportRecordImages(res);
          this.reportRecordData = { ...this.reportRecordData };
        });
        // the reason why again reassigning is to re generate the image which is not under the reportRecordData.
        // So when there is edit it was not calling
      });
  }
}
